import React, { useCallback, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useActionHook, useModals, useNavigate } from "hooks";
import {
  IApiServiceError,
  useCreateManyMeasureDataSelect,
  useDealActions,
  useDealIdSelector,
  useViewActions
} from "store";
import { CurrencyType, MeasureDataDraft } from "store/types";

import { Routes } from "../../../constants/routes";
import { Drawer } from "../Drawer/Drawer";
import { GroupsForm } from "../Groups-form/GroupsForm";
import { GroupFormDealData, MeasureDataGroup, MeasureDataRound } from "../Groups-form/GroupsFormContext";

const measureDataTables = [Routes.monitoringData, Routes.dealMonitoringData];
const measureDataDrafts = [Routes.monitoringDrafts, Routes.dealMonitoringDrafts];

export const CreateMany: React.FC = () => {
  const createMeasureData = useCreateManyMeasureDataSelect();
  const { setCreateManyMeasureData } = useViewActions();
  const {
    createManyMeasureDataAction,
    deleteMeasureDataDraftAction,
    saveMeasureDataDraftAction,
    getDealAction,
    getFoldersAction,
    getFilesAction,
    getDealRoundsWithInvestorsAndStoreAction
  } = useDealActions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const dealId = useDealIdSelector();
  const { errorModal } = useModals();

  const [dealData, setDealData] = useState<GroupFormDealData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { action: createManyMeasureData } = useActionHook(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await createManyMeasureDataAction(data);
      return data.groups.some((group) => {
        if (group.files && Object.keys(group.files).length > 0) {
          return true;
        }
        return group.data.some((measureData) => measureData.files && Object.keys(measureData.files).length > 0);
      });
    },
    {
      onSuccess: (res) => {
        if (res && dealId) {
          getFoldersAction({ dealId });
          getFilesAction(dealId);
        }
      }
    }
  );

  const getAndSetDealData = useCallback(
    async (dealId: number) => {
      try {
        setIsLoading(true);
        const { currency, parent, sub_assets } = await getDealAction(dealId);

        const currencies = currency?.data || [];

        setDealData({
          deal_id: dealId,
          defaultCurrency:
            currencies.length === 1
              ? (currencies[0].value as CurrencyType)
              : (currencies.find((curr) => curr.is_default)?.value as CurrencyType),
          subDeals: sub_assets.map(({ id, title }) => ({ id, title })),
          parentId: parent?.id || null
        });
      } catch (error) {
        errorModal({ msg: (error as IApiServiceError).message });
      } finally {
        setIsLoading(false);
      }
    },
    [getDealAction]
  );

  useEffect(() => {
    if (!createMeasureData) return setDealData(null);

    getAndSetDealData(createMeasureData.dealId);
  }, [createMeasureData]);

  const postMeasureData = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await createManyMeasureData(data);

      if (createMeasureData?.draftId) {
        await deleteMeasureDataDraftAction(createMeasureData?.draftId);
      }
      setCreateManyMeasureData(null);
      if (createMeasureData?.dealId) {
        await getDealRoundsWithInvestorsAndStoreAction(createMeasureData.dealId);
        queryClient.invalidateQueries(["round-investors", createMeasureData.dealId]);
      }

      if (measureDataTables.some((route) => matchPath(pathname, route))) return;

      if (createMeasureData?.dealId) {
        navigate({ route: "dealMonitoringData", params: { deal_id: createMeasureData.dealId } });
      } else {
        navigate({ route: "monitoringData" });
      }
    },
    [pathname, createMeasureData]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!createMeasureData?.dealId) return;

      await saveMeasureDataDraftAction({
        deal_id: createMeasureData.dealId,
        data,
        ...(createMeasureData.draftId && { id: createMeasureData.draftId })
      });

      setCreateManyMeasureData(null);

      if (measureDataDrafts.some((route) => matchPath(pathname, route))) return;

      if (createMeasureData?.dealId) {
        navigate({ route: "dealMonitoringDrafts", params: { deal_id: createMeasureData.dealId } });
      } else {
        navigate({ route: "monitoringDrafts" });
      }
    },
    [createMeasureData, pathname]
  );

  return (
    <Drawer
      open={Boolean(createMeasureData)}
      handleClose={() => setCreateManyMeasureData(null)}
      title={`Add Value - ${createMeasureData?.dealTitle}`}
    >
      <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={isLoading}>
        {createMeasureData && dealData && (
          <GroupsForm
            initialGroups={createMeasureData.initialGroups}
            initialMeasureData={createMeasureData.initialMeasureData}
            initialRoundsByDeal={createMeasureData.initialRounds}
            onSave={postMeasureData}
            onSaveAsDraft={handleSaveAsDraft}
            onClose={() => setCreateManyMeasureData(null)}
            {...dealData}
          />
        )}
      </AsyncContentMui>
    </Drawer>
  );
};
